/**
 * Idle-timeout timer.
 *
 * Watches user activity and, after `idleMs` of inactivity, runs `onIdle`. A
 * `warnMs` before that it runs `onWarn`, so the UI can show a "logging you out
 * soon" prompt with a live countdown. Any activity resets both timers and
 * clears the warning.
 *
 * Per-tab by design: each tab runs its own timers off its own activity. (A
 * shared-across-tabs version would coordinate via storage events; we chose not
 * to, so a tab left untouched logs out even if another tab is active.)
 *
 * Activity = pointer, key, scroll, or touch. These are passive listeners on
 * window, throttled so a busy mousemove doesn't reset state hundreds of times a
 * second.
 *
 * Sep-3: every throttled activity tick now ALSO writes a wall-clock
 * timestamp via `setLastActivity`, so the auth restore path can detect
 * idle sessions that would otherwise survive laptop sleep (the JS runtime
 * pauses during sleep, so setTimeout by itself misses that idle time).
 */
import { useEffect, useRef } from "react";
import { setLastActivity } from "@shared/api";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"] as const;

interface Options {
  /** Total inactivity before logout. */
  idleMs: number;
  /** How long before logout to fire the warning. */
  warnMs: number;
  /** Called once when the warning window opens. */
  onWarn: () => void;
  /** Called when idle time is reached. */
  onIdle: () => void;
  /** Called on any activity while the warning is showing (to dismiss it). */
  onActive: () => void;
  /** Master switch — timers only run while true (i.e. while logged in). */
  enabled: boolean;
}

export function useIdleTimeout({ idleMs, warnMs, onWarn, onIdle, onActive, enabled }: Options) {
  // Keep the latest callbacks without re-subscribing listeners each render.
  const cbs = useRef({ onWarn, onIdle, onActive });
  cbs.current = { onWarn, onIdle, onActive };

  const warnTimer = useRef<number | undefined>(undefined);
  const idleTimer = useRef<number | undefined>(undefined);
  const warning = useRef(false);
  const throttle = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const clearTimers = () => {
      window.clearTimeout(warnTimer.current);
      window.clearTimeout(idleTimer.current);
    };

    const arm = () => {
      clearTimers();
      warnTimer.current = window.setTimeout(() => {
        warning.current = true;
        cbs.current.onWarn();
      }, idleMs - warnMs);
      idleTimer.current = window.setTimeout(() => {
        cbs.current.onIdle();
      }, idleMs);
    };

    const onActivity = () => {
      // If a warning is up, any activity dismisses it immediately.
      if (warning.current) {
        warning.current = false;
        cbs.current.onActive();
      }
      // Throttle the reset to at most once per second.
      const now = Date.now();
      if (now - throttle.current < 1000) return;
      throttle.current = now;
      // Wall-clock heartbeat — persists to localStorage so a laptop-sleep
      // wake-up can detect stale sessions in AuthContext restore.
      setLastActivity(now);
      arm();
    };

    // Anchor a fresh timestamp when the timer starts — otherwise a login
    // that happens without any user activity would leave lastActivity stale
    // (or unset) until the first real event fires.
    setLastActivity();
    arm();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    return () => {
      clearTimers();
      warning.current = false;
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
    };
  }, [enabled, idleMs, warnMs]);
}
